import React, { useContext } from "react";
import { Routes, Route, Outlet, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";

/// Css
import "./index.css";
import "./chart.css";
import "./step.css";

/// Layout
import Nav from "./layouts/nav";
import ScrollToTop from "./layouts/ScrollToTop";
import Footer from "./layouts/Footer";

/// Dashboard
import Home from "./components/Dashboard/Home";

/// Bootstrap
import UiAlert from "./components/bootstrap/Alert";
import UiAccordion from "./components/bootstrap/Accordion";
import UiBadge from "./components/bootstrap/Badge";
import UiButton from "./components/bootstrap/Button";
import UiModal from "./components/bootstrap/Modal";
import UiButtonGroup from "./components/bootstrap/ButtonGroup";
import UiListGroup from "./components/bootstrap/ListGroup";
import UiCards from "./components/bootstrap/Cards";
import UiCarousel from "./components/bootstrap/Carousel";
import UiDropDown from "./components/bootstrap/DropDown";
import UiPopOver from "./components/bootstrap/PopOver";
import UiProgressBar from "./components/bootstrap/ProgressBar";
import UiTab from "./components/bootstrap/Tab";
import UiPagination from "./components/bootstrap/Pagination";
import UiGrid from "./components/bootstrap/Grid";
import UiTypography from "./components/bootstrap/Typography";

/// Pages
import { ThemeContext } from "../context/ThemeContext";
import { AdminSessions } from "./pages/admin/sessions";
import { TutorPersonalDetails } from "./pages/tutor/profile/personal-details";
import { TutorAvailability } from "./pages/tutor/profile/availability";
import { BrowseTutors } from "./pages/student/browseTutors";
import { StudentPersonalDetails } from "./pages/student/profile/personal-details";
import { StudentBillings } from "./pages/student/profile/billing";
import { StudentPaymentMethod } from "./pages/student/profile/payment-method";
import { StudentUpcomingSessions } from "./pages/student/sessions/upcoming";
import { StudentSessionsHistory } from "./pages/student/sessions/history";
import { StudentLearn } from "./pages/student/learn";
import { AdminTutorsListing } from "./pages/admin/users/tutors";
import { AdminAdminsListing } from "./pages/admin/users/admins";
import { AdminSessionDetails } from "./pages/admin/sessions/session-details";
import { TutorSessionsUpcoming } from "./pages/tutor/sessions/upcoming";
import { TutorSessionsHistory } from "./pages/tutor/sessions/history";
import { TutorSessionDetails } from "./pages/tutor/sessions/session-details";
import { ToastContainer } from "react-toastify";
import { BookSession } from "./pages/student/sessions/bookSession";
import { CheckoutPage } from "./pages/student/sessions/bookSession/checkout";
import { StudentSessionDetails } from "./pages/student/sessions/session-details";
import { TutorProfile } from "./pages/tutor/profile";
import { AdminChat } from "./pages/admin/chat";
import TransactionsTable from "./pages/admin/transactions";
import { UsersListing } from "./pages/admin/users";
import { AdminTutorProfile } from "./pages/admin/profile/tutor";
import { AdminRoleManagement } from "./pages/admin/roles";
import { SlotPricing } from "./pages/admin/slot-pricing";
import { AdminStudentProfile } from "./pages/admin/profile/student";
import { RolePermissions } from "./pages/admin/roles/role-permissions";
import { StudentPreferences } from "./pages/student/profile/preferences";
import { TutorPreferences } from "./pages/tutor/profile/preferences";
import { StudentChat } from "./pages/student/chat";
import { TutorChat } from "./pages/tutor/chat";
import TutorDashboard from "./pages/tutor/dashboard"
import AdminDashboard from "./pages/admin/dashboard"
import StudentDashboard from "./pages/student/dashboard";
import { RescheduleSession } from "./pages/student/sessions/RescheduleSession";

const Markup = () => {
    const { role, isAuthenticated } = useSelector((state) => state.auth);

    const superAdminRoutes = [
        { url: "dashboard", component: <Home /> },
        { url: "sessions", component: <AdminSessions /> },
        { url: "sessions/:id", component: <AdminSessionDetails /> },
        // users
        { url: "tutors-list", component: <AdminTutorsListing /> },
        { url: "admins-list", component: <AdminAdminsListing /> },
        { url: "users-list", component: <UsersListing /> },
        { url: "chat", component: <AdminChat /> },
        { url: "transactions", component: <TransactionsTable /> },

        { url: "tutor/:id", component: <AdminTutorProfile /> },
        { url: "student/:id", component: <AdminStudentProfile /> },
        { url: "roles", component: <AdminRoleManagement /> },
        { url: "slot-pricing", component: <SlotPricing /> },
        { url: "roles/role-permissions/:id", component: <RolePermissions /> },
    ];
    const adminRoutes = [
        { url: "dashboard", component: <AdminDashboard /> },
        { url: "sessions", component: <AdminSessions /> },
        { url: "sessions/:id", component: <AdminSessionDetails /> },
        { url: "sessions/sessionDetails/:sessionId", component: <StudentSessionDetails /> },
        { url: "reschedule-session/:sessionId/:tutorId", component: <RescheduleSession /> },
        // users
        { url: "tutors-list", component: <AdminTutorsListing /> },
        { url: "admins-list", component: <AdminAdminsListing /> },
        { url: "users-list", component: <UsersListing /> },
        { url: "chat", component: <AdminChat /> },
        { url: "transactions", component: <TransactionsTable /> },

        { url: "tutor/:id", component: <AdminTutorProfile /> },
        { url: "student/:id", component: <AdminStudentProfile /> },
         { url: "roles", component: <AdminRoleManagement /> },
         { url: "slot-pricing", component: <SlotPricing /> },
         { url: "roles/role-permissions/:id", component: <RolePermissions /> },
    ];

    const studentRoutes = [
        { url: "dashboard", component: <StudentDashboard /> },

        // lesson library
        { url: "learn", component: <StudentLearn /> },

        // session information
        { url: "sessions/upcoming", component: <StudentUpcomingSessions /> },
        { url: "sessions/history", component: <StudentSessionsHistory /> },

        // session booking
        { url: "browse-tutors", component: <BrowseTutors /> },

        // profile
        { url: "personal-details", component: <StudentPersonalDetails /> },
        { url: "billings", component: <StudentBillings /> },
        { url: "payment-methods", component: <StudentPaymentMethod /> },
        { url: "tutor-profile/:id", component: <TutorProfile /> },
        { url: "book-session", component: <BookSession /> },
        { url: "reschedule-session/:sessionId/:tutorId", component: <RescheduleSession /> },    
        { url: "checkout", component: <CheckoutPage /> },
        { url: "sessions/sessionDetails/:sessionId", component: <StudentSessionDetails /> },
        { url: "preferences", component: <StudentPreferences /> },
        { url: "chat", component: <StudentChat /> }
    ];

    const tutorRoutes = [
        { url: "dashboard", component: <TutorDashboard /> },

        //sessions
        { url: "upcoming-sessions", component: <TutorSessionsUpcoming /> },
        { url: "sessions-history", component: <TutorSessionsHistory /> },
        { url: "sessions/sessionDetails/:sessionId", component: <TutorSessionDetails /> },

        //profile
        { url: "personal-details", component: <TutorPersonalDetails /> },
        { url: "availability", component: <TutorAvailability /> },
        { url: "preferences", component: <TutorPreferences /> },
        { url: "chat", component: <TutorChat /> }
    ];
    const routhPath = [
        //bootstrap
        { url: "ui-modal", component: <UiModal /> },
        { url: "ui-popover", component: <UiPopOver /> },
        { url: "ui-typography", component: <UiTypography /> },
        { url: "ui-grid", component: <UiGrid /> },
    ];
    const routhPath2 = [
        //Bootstrap
        { url: "ui-accordion", component: <UiAccordion /> },
        { url: "ui-alert", component: <UiAlert /> },
        { url: "ui-badge", component: <UiBadge /> },
        { url: "ui-button", component: <UiButton /> },
        { url: "ui-button-group", component: <UiButtonGroup /> },
        { url: "ui-list-group", component: <UiListGroup /> },
        { url: "ui-card", component: <UiCards /> },
        { url: "/ui-carousel", component: <UiCarousel /> },
        { url: "/ui-dropdown", component: <UiDropDown /> },
        { url: "/ui-progressbar", component: <UiProgressBar /> },
        { url: "/ui-tab", component: <UiTab /> },
        { url: "/ui-pagination", component: <UiPagination /> },
    ];

    const routes = () => {
        switch (role) {
            case "Admin":
                return adminRoutes;
            case "nkenne":
                return superAdminRoutes;
            case "Student":
                return studentRoutes;
            case "Tutor":
                return tutorRoutes;
            default:
                return [];
        }
    };
    return (
        <>
            <Routes>
                <Route element={<MainLayout />}>
                    {routes().map(({ url, component }) => (
                        <Route exact key={url} path={url} element={component} />
                    ))}
                    <Route path="*" element={<Navigate to="dashboard" />} />
                </Route>
            </Routes>
            <ScrollToTop />
        </>
    );
};

function MainLayout() {
    const sideMenu = useSelector((state) => state.sideMenu);
    const { sidebariconHover } = useContext(ThemeContext);
    return (
        <>
            <ToastContainer />
            <div
                id="main-wrapper"
                className={`show ${
                    sidebariconHover ? "iconhover-toggle" : ""
                } ${sideMenu ? "menu-toggle" : ""}`}
            >
                <Nav />
                <div
                    className="content-body"
                    // style={{ minHeight: window.screen.height + 20 }}
                >
                    <div className="container-fluid pt-0">   
                        <Outlet />
                    </div>
                </div>
                {/* <Footer changeFooter="out-footer style-1" /> */}
            </div>
        </>
    );
}

export default Markup;
